// screens/marketplace/CourseList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GraduationCap,
  Search,
  RefreshCw,
  AlertCircle,
  ArrowRight,
  ShoppingBag,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import StorefrontHeader from "../../components/StorefrontHeader";
import StorefrontNav from "../../components/StorefrontNav";
import { listCourses } from "./coursesApi";

const money = (v) => `₵${Number(v || 0).toFixed(2)}`;

export default function CourseList() {
  const { authFetch } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const load = useCallback(
    async ({ silent, search } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const data = await listCourses(authFetch, { search });
        setCourses(data);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authFetch, showToast]
  );

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = useCallback(
    (q) => {
      setSearchInput(q);
      load({ search: q });
    },
    [load]
  );

  const isEmpty = !loading && courses.length === 0;
  const hasSearch = searchInput.trim().length > 0;

  return (
    <div style={styles.pageWrap}>
      <StorefrontHeader
        title="Digital courses"
        subtitle="Learn a skill. Buy once, keep forever."
        onSearch={onSearch}
        searchValue={searchInput}
        searchPlaceholder="Search courses…"
      />

      <div style={{ marginBottom: 14 }}>
        <StorefrontNav />
      </div>

      <div style={styles.toolbar}>
        <div style={styles.toolbarCount(colors)}>
          {loading
            ? "Loading courses…"
            : `${courses.length} course${courses.length === 1 ? "" : "s"}`}
          {hasSearch && !loading && (
            <>
              {" · "}
              <strong style={{ color: colors.text }}>“{searchInput}”</strong>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => load({ silent: true, search: searchInput })}
          disabled={refreshing}
          style={styles.ghostBtn(colors)}
        >
          <RefreshCw
            size={13}
            style={{
              animation: refreshing ? "spin 1s linear infinite" : "none",
            }}
          />
          Refresh
        </button>
      </div>

      {loading && (
        <div
          style={styles.loadingStrip(colors)}
          role="status"
          aria-live="polite"
        >
          <span style={styles.loadingDot(colors)} />
          <span>Loading courses…</span>
        </div>
      )}

      {loading ? (
        <SkeletonCourseGrid count={4} colors={colors} />
      ) : isEmpty ? (
        <EmptyState
          colors={colors}
          hasSearch={hasSearch}
          onClear={() => onSearch("")}
          onBrowse={() => navigate("/marketplace")}
        />
      ) : (
        <div className="cl-grid">
          {courses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              colors={colors}
              onOpen={() => navigate(`/courses/${course.id}`)}
            />
          ))}
        </div>
      )}

      <style>{`
        .cl-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          gap: 18px;
        }
        @media (min-width: 720px) {
          .cl-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (min-width: 1100px) {
          .cl-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes cl-skeleton {
          0%, 100% { opacity: 0.65; }
          50% { opacity: 0.30; }
        }
        @keyframes cl-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Course card — course-specific, 16:9 cover
   ══════════════════════════════════════════════════════════ */

const CourseCard = ({ course, colors, onOpen }) => {
  const price = course.effective_price || course.price;

  return (
    <article
      onClick={onOpen}
      style={styles.card(colors)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.borderColor = colors.btnBg;
        e.currentTarget.style.boxShadow = `0 16px 40px ${colors.shadow}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = colors.border;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={styles.cardMedia(colors)}>
        {course.cover_image_url ? (
          <img
            src={course.cover_image_url}
            alt={course.title}
            loading="lazy"
            style={styles.cardImg}
          />
        ) : (
          <div style={styles.cardImgFallback(colors)}>
            <GraduationCap size={38} />
          </div>
        )}

        <span style={styles.cardBadge(colors)}>
          <GraduationCap size={10} />
          Course
        </span>
      </div>

      <div style={styles.cardBody}>
        <h3 style={styles.cardTitle(colors)}>{course.title}</h3>

        {course.short_description && (
          <p style={styles.cardDesc(colors)}>{course.short_description}</p>
        )}

        <div style={styles.cardFooter}>
          <span style={styles.cardPrice(colors)}>{money(price)}</span>
          <span style={styles.cardCta(colors)}>
            View course
            <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </article>
  );
};

/* ══════════════════════════════════════════════════════════
   Skeleton / Empty
   ══════════════════════════════════════════════════════════ */

const SkeletonCourseGrid = ({ count, colors }) => (
  <div className="cl-grid">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        style={{
          borderRadius: 16,
          border: `1px solid ${colors.border}`,
          background: colors.raisedBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            aspectRatio: "16 / 9",
            background: colors.hoverBg,
            animation: "cl-skeleton 1.6s ease-in-out infinite",
          }}
        />
        <div style={{ padding: 16, display: "grid", gap: 8 }}>
          <div
            style={{
              height: 14,
              borderRadius: 4,
              background: colors.hoverBg,
              width: "70%",
              animation: "cl-skeleton 1.6s ease-in-out infinite",
            }}
          />
          <div
            style={{
              height: 12,
              borderRadius: 4,
              background: colors.hoverBg,
              width: "90%",
              animation: "cl-skeleton 1.6s ease-in-out infinite",
            }}
          />
          <div
            style={{
              height: 12,
              borderRadius: 4,
              background: colors.hoverBg,
              width: "45%",
              animation: "cl-skeleton 1.6s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ colors, hasSearch, onClear, onBrowse }) => (
  <div style={styles.emptyWrap(colors)}>
    <div style={styles.emptyIcon(colors)}>
      <GraduationCap size={26} />
    </div>
    <h3 style={styles.emptyTitle(colors)}>
      {hasSearch ? "No courses match" : "Courses are coming soon"}
    </h3>
    <p style={styles.emptyText(colors)}>
      {hasSearch
        ? `Nothing matches “${hasSearch}”. Try a different term.`
        : "The first courses are being prepared. Meanwhile, browse the marketplace."}
    </p>
    <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
      {hasSearch && (
        <button type="button" onClick={onClear} style={styles.emptySecondary(colors)}>
          Clear search
        </button>
      )}
      <button type="button" onClick={onBrowse} style={styles.emptyPrimary(colors)}>
        <ShoppingBag size={14} />
        Browse marketplace
      </button>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "20px 16px 60px",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  toolbarCount: (c) => ({
    fontSize: 12.5,
    color: c.secondaryText,
  }),
  ghostBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 12px",
    borderRadius: 9,
    border: `1px solid ${c.border}`,
    background: "transparent",
    color: c.text,
    cursor: "pointer",
    fontSize: 12.5,
    fontWeight: 600,
  }),

  loadingStrip: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 10,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    color: c.secondaryText,
    fontSize: 12.5,
    fontWeight: 600,
    marginBottom: 16,
  }),
  loadingDot: (c) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: c.btnBg,
    animation: "cl-pulse 1.2s ease-in-out infinite",
    flexShrink: 0,
  }),

  card: (c) => ({
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    overflow: "hidden",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    transition:
      "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
  }),
  cardMedia: (c) => ({
    position: "relative",
    aspectRatio: "16 / 9",
    background: c.hoverBg,
    overflow: "hidden",
  }),
  cardImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  cardImgFallback: (c) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: c.secondaryText,
    background: `linear-gradient(135deg, ${c.hoverBg}, ${c.subtleBg || c.hoverBg})`,
  }),
  cardBadge: (c) => ({
    position: "absolute",
    top: 12,
    left: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 10px",
    borderRadius: 999,
    background: c.raisedBg,
    color: c.btnBg,
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    boxShadow: `0 2px 8px ${c.shadow}`,
  }),

  cardBody: {
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: 1,
  },
  cardTitle: (c) => ({
    margin: 0,
    fontSize: 17,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.01em",
    lineHeight: 1.3,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    minHeight: 44,
  }),
  cardDesc: (c) => ({
    margin: 0,
    fontSize: 13,
    color: c.secondaryText,
    lineHeight: 1.55,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    minHeight: 40,
  }),
  cardFooter: {
    marginTop: "auto",
    paddingTop: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  cardPrice: (c) => ({
    fontSize: 20,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.02em",
  }),
  cardCta: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 13,
    fontWeight: 700,
    color: c.btnBg,
  }),

  emptyWrap: (c) => ({
    textAlign: "center",
    padding: "60px 24px",
    borderRadius: 16,
    border: `1px dashed ${c.border}`,
    background: c.raisedBg,
  }),
  emptyIcon: (c) => ({
    width: 64,
    height: 64,
    borderRadius: 18,
    background: c.hoverBg,
    color: c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  }),
  emptyTitle: (c) => ({
    margin: "0 0 6px",
    fontSize: 17,
    fontWeight: 800,
    color: c.heading,
  }),
  emptyText: (c) => ({
    margin: "0 auto 22px",
    fontSize: 13.5,
    color: c.secondaryText,
    maxWidth: 420,
    lineHeight: 1.55,
  }),
  emptySecondary: (c) => ({
    padding: "10px 18px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: "transparent",
    color: c.text,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
  }),
  emptyPrimary: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 18px",
    borderRadius: 10,
    border: "none",
    background: c.btnBg,
    color: c.btnText,
    fontSize: 13.5,
    fontWeight: 700,
    cursor: "pointer",
  }),
};